import React, { useState, useCallback } from 'react';

import dashboardApi from 'services/apis/dashboard';
import { Table, Text, Link, Button } from 'v3/components';
import { formatDateTime, formatPlate } from 'v3/utils/formatter';
import { useQuery } from 'hooks';
import Card, { Number, Description, SeeAll } from './Card';

export default function TravelsPendingVehicleChecklist({ dashboard, name, id }) {
  const [data, setData] = useState({});
  const [filter, setFilter] = useState({});
  const [currentPage, setCurrentPage] = useState({});
  const [pagination, setPagination] = useState({});
  const [isFetching, setFetching] = useState(false);
  const [loading, setLoading] = useState(false);

  const queryParams = useQuery();

  function setQueryParams(filters) {
    const { tags = [], start_date, end_date } = filters;

    queryParams.delete('tags');

    if (tags) {
      tags.forEach((tag) => {
        queryParams.append('tags', tag.id);
      });
    }

    queryParams.set('status', 10);

    if (start_date) {
      queryParams.set('start_date', start_date);
    }
    if (end_date) {
      queryParams.set('end_date', end_date);
    }

    if (tags?.length === 0) {
      queryParams.set('no-tags', true);
    } else {
      queryParams.delete('no-tags');
    }
  }

  const fetchAvailablesLoads = useCallback(async (filters = {}) => {
    try {
      setLoading(true);

      const {
        tags = [],
        clients = [],
        negotiators = [],
        costCenters = [],
        pcps = [],
        companies = [],
        page = 1,
        start_date,
        end_date,
      } = filters;

      if (page === 1) {
        setFetching(true);
      }

      setQueryParams(filters);
      setFilter(filters);
      setCurrentPage(+page);

      const response = await dashboardApi.get('/travels-pending-vehicle-checklist', {
        params: {
          tags: tags?.map((tag) => tag.id),
          clients: clients?.map((client) => client.id),
          start_date,
          negotiators: negotiators?.map((negotiator) => negotiator?.id),
          costCenters: costCenters?.map((costCenter) => costCenter?.id),
          pcps: pcps?.map((pcp) => pcp?.id),
          companies: companies?.map((company) => company?.id),
          end_date: end_date || undefined,
          page,
        },
      });

      setPagination(response.data.last?.pagination);

      if (page === 1) {
        setData(response.data);
      } else {
        setData((oldData) => ({
          ...oldData,
          last: {
            ...oldData.last,
            data: [...oldData.last.data, ...response.data.last.data],
          },
        }));
      }
    } catch (error) {
      // comment
    } finally {
      setFetching(false);
      setLoading(false);
    }
  }, []);

  return (
    <>
      <Card
        title="Viagens com checklist de veiculo pendente"
        isLoading={isFetching}
        fetchFn={fetchAvailablesLoads}
        onFiltersChanged={fetchAvailablesLoads}
        dashboard={dashboard}
        name={name}
        excludeFilterByBranchOffice
        id={id}
        ListComponent={
          <>
            <Table striped>
              <thead>
                <tr>
                  <th>
                    <Text weight="500" color="dark">
                      Centro de custo
                    </Text>
                  </th>
                  <th>
                    <Text weight="500" color="dark">
                      Empresa
                    </Text>
                  </th>
                  <th>
                    <Text weight="500" color="dark">
                      Tags da carga
                    </Text>
                  </th>
                  <th>
                    <Text weight="500" color="dark">
                      Nº Pedido Cliente
                    </Text>
                  </th>
                  <th>
                    <Text weight="500" color="dark">
                      Viagem
                    </Text>
                  </th>
                  <th>
                    <Text weight="500" color="dark">
                      Negociador
                    </Text>
                  </th>
                  <th>
                    <Text weight="500" color="dark">
                      Data Máxima Carregamento
                    </Text>
                  </th>
                  <th>
                    <Text weight="500" color="dark">
                      Motorista
                    </Text>
                  </th>
                  <th>
                    <Text weight="500" color="dark">
                      Placas
                    </Text>
                  </th>
                  <th>
                    <Text weight="500" color="dark">
                      Cliente
                    </Text>
                  </th>
                  <th>
                    <Text weight="500" color="dark">
                      Origem
                    </Text>
                  </th>
                  <th>
                    <Text weight="500" color="dark">
                      Destino
                    </Text>
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.last?.data?.map((load) => {
                  const title = [
                    `Cliente ${load.social_name} - ${load.cgccpf}`,
                    `Motorista ${load.driver_name}`,
                    `Placa veículo tração ${formatPlate(load.plates)}`,
                  ].join('\n');

                  return (
                    <tr>
                      <td>
                        <Text type="regular" color="gray">
                          {load?.cost_center || '-'}
                        </Text>
                      </td>
                      <td>
                        <Text type="regular" color="gray">
                          {load?.company_name || '-'}
                        </Text>
                      </td>
                      <td>
                        <Text type="regular" color="gray">
                          {load?.load_tags || '-'}
                        </Text>
                      </td>
                      <td>
                        <Text type="regular" color="gray">
                          {load?.client_order_number || '-'}
                        </Text>
                      </td>
                      <td>
                        <Link to={`/viagens/${load.attended_id}`} withoutIcon target="_blank">
                          {load.attended_id}
                        </Link>
                      </td>
                      <td>
                        <Text type="regular" color="gray">
                          {load.user_created}
                        </Text>
                      </td>
                      <td>
                        <Text type="regular" color="gray">
                          {formatDateTime(load.scheduled_origin)}
                        </Text>
                      </td>
                      <td>
                        <Link to={`/motoristas/${load.driver_id}`} withoutIcon target="_blank">
                          {load.driver_name}
                        </Link>
                      </td>
                      <td>
                        <Link to={`/veiculos/${load.vehicle_id}`} withoutIcon target="_blank">
                          {load.plates}
                        </Link>
                      </td>
                      <td>
                        <Link to={`/clientes/${load.person_client_id}`} withoutIcon target="_blank">
                          {load.social_name}
                        </Link>
                      </td>
                      <td>
                        <Text type="regular" color="gray" truncate>
                          {`${load?.city_origin} - ${load.uf_origin}`}
                        </Text>
                      </td>
                      <td>
                        <Text type="regular" color="gray" truncate>
                          {`${load?.city_destination} - ${load.uf_destination}`}
                        </Text>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            {pagination.lastPage > 1 && pagination.to < pagination.total && (
              <div className="text-center mt-3 col-12">
                <Button
                  className="mt-2 ml-auto align-item-center"
                  onClick={() => {
                    fetchAvailablesLoads({
                      page: currentPage + 1,
                      tags: filter.tags,
                      clients: filter.clients,
                      start_date: filter?.start_date,
                      end_date: filter?.end_date,
                      costCenters: filter?.costCenters,
                      pcps: filter?.pcps,
                      negotiators: filter?.negotiators,
                      companies: filter?.companies,
                    });
                  }}
                  loading={loading}
                >
                  <Text type="regular" color="white" truncate>
                    Ver mais
                  </Text>
                </Button>
              </div>
            )}
            {!!data.total && (
              <SeeAll
                to={{
                  pathname: '/viagens',
                  search: queryParams.toString(),
                }}
              >
                ver todas as {data.total} viagens
              </SeeAll>
            )}
          </>
        }
        NumericComponent={
          <div className="d-flex justify-content-center text-center flex-column">
            <Number color="warning">{data.total}</Number>
            <Description>Viagens com checklist de veiculo pendente</Description>
          </div>
        }
      />
    </>
  );
}
